import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChargingStation } from '@/types/task';
import { initialStations } from '@/data/stations';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PaymentMethods } from '@/components/PaymentMethods';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { DateTimeSelector } from '@/components/booking/DateTimeSelector';
import { PricingSummary } from '@/components/booking/PricingSummary';

export default function BookingPage() {
  const { id } = useParams<{ id: string }>();
  const [station, setStation] = useState<ChargingStation | null>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Mock authentication check - Replace this with your actual auth check
  const isAuthenticated = false; // This should be replaced with your actual auth check

  useEffect(() => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to make a booking.",
        variant: "destructive"
      });
      navigate('/login', { state: { from: `/book/${id}` } });
      return;
    }

    const foundStation = initialStations.find(s => s.id === id);
    if (foundStation) {
      setStation(foundStation);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    if (station && duration > 0) {
      const estimatedKWh = station.powerOutput * duration;
      const basePrice = estimatedKWh * station.price;
      
      const serviceFee = basePrice * 0.05;
      
      setTotalPrice(basePrice + serviceFee);
    }
  }, [station, duration]);

  const handleBookNow = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to make a booking.",
        variant: "destructive"
      });
      navigate('/login', { state: { from: `/book/${id}` } });
      return;
    }

    if (!date || !startTime) {
      toast({
        title: "Booking incomplete",
        description: "Please select a date and time for your booking.",
        variant: "destructive"
      });
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentSuccess = () => {
    toast({
      title: "Booking confirmed!",
      description: `Your charging session has been booked for ${format(date!, 'PP')} at ${startTime}.`,
    });
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  if (!station) {
    return (
      <div className="container py-8 text-center">
        <h1 className="text-2xl font-bold">Station not found</h1>
        <p className="mt-4">The charging station you're looking for doesn't exist.</p>
        <Button className="mt-6" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Book a Charging Session</CardTitle>
          <CardDescription>
            Choose when you want to charge your EV at {station.title}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!showPayment ? (
            <>
              <div className="flex flex-col md:flex-row gap-6">
                <DateTimeSelector
                  date={date}
                  setDate={setDate}
                  startTime={startTime}
                  setStartTime={setStartTime}
                  duration={duration}
                  setDuration={setDuration}
                />
                <PricingSummary
                  station={station}
                  duration={duration}
                  totalPrice={totalPrice}
                />
              </div>

              <div className="border-t pt-4">
                <Button className="w-full md:w-auto" size="lg" onClick={handleBookNow}>
                  Continue to Payment
                </Button>
              </div>
            </>
          ) : (
            <PaymentMethods 
              amount={totalPrice} 
              onSuccess={handlePaymentSuccess} 
              onCancel={() => setShowPayment(false)} 
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
